import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { finalize, forkJoin, Observable, Subscription, tap } from 'rxjs';
import { AutoCompleteModel } from '../../../core/component/autocomplete/autocomplete-model';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { ErrandType } from '../../../round-planning/model/const/enums';
import { ErrandCategory } from '../../../standalone-errand/model/errand-category';
import {
  ErrandAssignmentOption,
  ErrandAssignmentOptions,
  PropertyErrandSubCategoryResponsibility,
} from '../../../standalone-errand/model/errand-tag';
import { ErrandTagService } from '../../../standalone-errand/service/data/errand-tag.service';
import { ErrandCategoryService } from '../../../standalone-errand/service/data/errand-category.service';

interface ResponsibilityRowContext {
  form: FormGroup;
  historicalReadonly: boolean;
  savedSubcategoryCaption?: string;
  subcategorySelection: SelectionItem<string>[];
  resources: AutoCompleteModel[];
  executorGroups: AutoCompleteModel[];
  executorWebUsers: AutoCompleteModel[];
  subscriptions: Subscription;
  optionRequest?: Subscription;
}

@Component({
  selector: 'app-property-errand-subcategory-responsibility',
  templateUrl: './property-errand-subcategory-responsibility.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PropertyErrandSubCategoryResponsibilityComponent implements OnChanges, OnDestroy {
  @Input() propertyId: string;

  rows: ResponsibilityRowContext[] = [];
  loading = false;
  saving = false;
  private structureDirty = false;
  private categories: ErrandCategory[] = [];
  private readonly subscriptions = new Subscription();
  private readonly duplicateSubcategoryValidator: ValidatorFn = control => {
    const subcategoryId = control.value;
    if (!subcategoryId) return null;

    const duplicated = this.rows.some(
      row => row.form.controls['errandSubCategoryId'] !== control
        && row.form.controls['errandSubCategoryId'].value === subcategoryId
    );
    return duplicated
      ? { duplicateResponsibilitySubcategory: { message: 'ERRAND_TAG.DUPLICATE_RESPONSIBILITY' } }
      : null;
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly controls: FormControlExFactory,
    private readonly service: ErrandTagService,
    private readonly categoryService: ErrandCategoryService,
    private readonly messages: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertyId'] && this.propertyId) this.load();
  }

  ngOnDestroy(): void {
    this.clearRows();
    this.subscriptions.unsubscribe();
  }

  add(): void {
    this.rows.push(this.createRow());
    this.structureDirty = true;
    this.refreshSubcategoryState();
  }

  remove(index: number): void {
    if (this.rows[index].historicalReadonly) return;

    this.rows[index].optionRequest?.unsubscribe();
    this.rows[index].subscriptions.unsubscribe();
    this.rows.splice(index, 1);
    this.structureDirty = true;
    this.refreshSubcategoryState();
  }

  get canAddResponsibility(): boolean {
    const selectedSubcategoryIds = new Set(
      this.rows.map(row => row.form.controls['errandSubCategoryId'].value).filter(Boolean)
    );
    return this.categories.some(category =>
      this.isAssignableCategory(category)
      && category.subcategories.some(subcategory =>
        subcategory.isEnabled && !selectedSubcategoryIds.has(subcategory.id)
      )
    );
  }

  get invalid(): boolean {
    return this.rows.some(row => row.form.invalid);
  }

  get dirty(): boolean {
    return this.structureDirty || this.rows.some(row => row.form.dirty);
  }

  validate(): boolean {
    this.refreshDuplicateValidation();
    if (!this.invalid) return true;

    this.rows.forEach(row => FormComponentBase.markAsTouched(row.form));
    return false;
  }

  saveChanges(): Observable<PropertyErrandSubCategoryResponsibility[]> {
    this.saving = true;
    return this.service.saveResponsibilities(this.propertyId, this.toPayload()).pipe(
      tap(saved => this.replaceRows(saved)),
      finalize(() => (this.saving = false))
    );
  }

  private load(): void {
    this.loading = true;
    this.subscriptions.add(
      forkJoin({
        categories: this.categoryService.getList(),
        responsibilities: this.service.getResponsibilities(this.propertyId),
      }).subscribe({
        next: result => {
          this.loading = false;
          this.categories = result.categories ?? [];
          this.replaceRows(result.responsibilities);
        },
        error: error => {
          this.loading = false;
          this.messages.showError(error);
        },
      })
    );
  }

  private replaceRows(models: PropertyErrandSubCategoryResponsibility[]): void {
    this.clearRows();
    this.rows = (models ?? []).map(model => this.createRow(model));
    this.structureDirty = false;
    this.refreshSubcategoryState();
  }

  private clearRows(): void {
    this.rows.forEach(row => {
      row.optionRequest?.unsubscribe();
      row.subscriptions.unsubscribe();
    });
    this.rows = [];
  }

  private createRow(model?: PropertyErrandSubCategoryResponsibility): ResponsibilityRowContext {
    const historicalReadonly = this.isHistoricalReadonly(model);
    const subcategoryControl = this.controls.createMandatory(
      'ERRAND_TAG.ERRAND_CATEGORY_SUBCATEGORY',
      model?.errandSubCategoryId
    );
    const resourceControl = this.controls.create(
      'FAULT_NOTIFICATION.RESOURCE',
      this.savedOption(model?.resourceName, model?.resourceId)
    );
    const executorGroupControl = this.controls.create(
      'ERRANDLIST_EXECUTORGROUPS',
      this.savedOption(model?.executorGroupName, model?.executorGroupId)
    );
    const executorWebUserControl = this.controls.create(
      'ERRANDLIST_WEBUSERS',
      this.savedOption(model?.executorWebUserName, model?.executorWebUserId)
    );
    subcategoryControl.addValidators(this.duplicateSubcategoryValidator);
    [subcategoryControl, resourceControl, executorGroupControl, executorWebUserControl]
      .forEach(control => (control.isReadonly = historicalReadonly));
    const context: ResponsibilityRowContext = {
      form: this.fb.group({
        id: this.controls.createHidden(model?.id),
        errandSubCategoryId: subcategoryControl,
        resourceId: resourceControl,
        executorGroupId: executorGroupControl,
        executorWebUserId: executorWebUserControl,
      }),
      historicalReadonly,
      savedSubcategoryCaption: model
        ? this.subcategoryCaption(model.errandCategoryName, model.errandSubCategoryName)
        : undefined,
      subcategorySelection: [],
      resources: resourceControl.value ? [resourceControl.value] : [],
      executorGroups: executorGroupControl.value ? [executorGroupControl.value] : [],
      executorWebUsers: executorWebUserControl.value ? [executorWebUserControl.value] : [],
      subscriptions: new Subscription(),
    };

    if (historicalReadonly) return context;

    context.subscriptions.add(
      context.form.controls['errandSubCategoryId'].valueChanges.subscribe(subcategoryId => {
        context.form.patchValue({ resourceId: null, executorGroupId: null, executorWebUserId: null }, { emitEvent: false });
        this.loadOptions(context, subcategoryId);
        this.refreshSubcategoryState();
      })
    );
    context.subscriptions.add(
      context.form.controls['executorGroupId'].valueChanges.subscribe(groupId => {
        const currentResource = context.form.controls['resourceId'].value;
        const currentUser = context.form.controls['executorWebUserId'].value;
        this.loadOptions(
          context,
          context.form.controls['errandSubCategoryId'].value,
          groupId?.id,
          currentUser?.id,
          currentResource?.id
        );
      })
    );

    if (model?.errandSubCategoryId) {
      this.loadOptions(
        context,
        model.errandSubCategoryId,
        model.executorGroupId,
        model.executorWebUserId,
        model.resourceId,
        true
      );
    }
    return context;
  }

  private isHistoricalReadonly(model?: PropertyErrandSubCategoryResponsibility): boolean {
    if (!model?.errandSubCategoryId) return false;

    const category = this.categories.find(item =>
      item.subcategories.some(subcategory => subcategory.id === model.errandSubCategoryId)
    );
    const subcategory = category?.subcategories.find(item => item.id === model.errandSubCategoryId);
    return !this.isAssignableCategory(category) || !subcategory?.isEnabled;
  }

  private savedOption(name?: string, id?: string): AutoCompleteModel | null {
    return id ? new AutoCompleteModel(name || id, id) : null;
  }

  private refreshSubcategoryState(): void {
    const selectedSubcategoryIds = new Set(
      this.rows.map(row => row.form.controls['errandSubCategoryId'].value).filter(Boolean)
    );
    this.rows.forEach(row => {
      const currentSubcategoryId = row.form.controls['errandSubCategoryId'].value;
      if (row.historicalReadonly) {
        const modelCategory = this.categories.find(category =>
          category.subcategories.some(subcategory => subcategory.id === currentSubcategoryId)
        );
        const modelSubcategory = modelCategory?.subcategories.find(item => item.id === currentSubcategoryId);
        row.subcategorySelection = currentSubcategoryId
          ? [new SelectionItem(
              modelCategory && modelSubcategory
                ? this.subcategoryCaption(modelCategory.currentLanguageName, modelSubcategory.currentLanguageName)
                : row.savedSubcategoryCaption || currentSubcategoryId,
              currentSubcategoryId
            )]
          : [];
        return;
      }

      row.subcategorySelection = this.categories
        .filter(category => this.isAssignableCategory(category))
        .flatMap(category => category.subcategories
          .filter(subcategory => subcategory.isEnabled
            && (subcategory.id === currentSubcategoryId || !selectedSubcategoryIds.has(subcategory.id)))
          .map(subcategory => new SelectionItem(
            this.subcategoryCaption(category.currentLanguageName, subcategory.currentLanguageName),
            subcategory.id
          )))
        .sort((left, right) => left.name.localeCompare(right.name));
    });
    this.refreshDuplicateValidation();
  }

  private refreshDuplicateValidation(): void {
    this.rows.forEach(row =>
      row.form.controls['errandSubCategoryId'].updateValueAndValidity({ emitEvent: false })
    );
  }

  private isAssignableCategory(category?: ErrandCategory): boolean {
    return !!category?.isEnabled;
  }

  private subcategoryCaption(categoryName?: string, subcategoryName?: string): string {
    return [categoryName, subcategoryName].filter(Boolean).join(' / ');
  }

  private loadOptions(
    context: ResponsibilityRowContext,
    subcategoryId?: string,
    executorGroupId?: string,
    currentExecutorWebUserId?: string,
    currentResourceId?: string,
    preserveMissingCurrentOptions = false
  ): void {
    context.optionRequest?.unsubscribe();
    if (!subcategoryId) {
      context.resources = [];
      context.executorGroups = [];
      context.executorWebUsers = [];
      return;
    }

    const request = (errandType: ErrandType) =>
      this.service.getConfigurationAssignmentOptions(
        this.propertyId,
        this.defaultTagId(subcategoryId),
        errandType,
        executorGroupId
      );
    const currentResource = preserveMissingCurrentOptions ? context.form.controls['resourceId'].value : null;
    const currentExecutorGroup = preserveMissingCurrentOptions ? context.form.controls['executorGroupId'].value : null;
    const currentExecutorWebUser = preserveMissingCurrentOptions ? context.form.controls['executorWebUserId'].value : null;
    context.optionRequest = forkJoin([request(ErrandType.TenantRequest), request(ErrandType.GenericErrand)]).subscribe({
        next: options => {
          context.resources = this.mergeOptions(options, item => item.resources);
          context.executorGroups = this.mergeOptions(options, item => item.executorGroups);
          context.executorWebUsers = this.mergeOptions(options, item => item.executorWebUsers);
          context.form.patchValue({
            resourceId: this.findOption(context.resources, currentResourceId, currentResource),
            executorGroupId: this.findOption(context.executorGroups, executorGroupId, currentExecutorGroup),
            executorWebUserId: this.findOption(context.executorWebUsers, currentExecutorWebUserId, currentExecutorWebUser),
          }, { emitEvent: false });
        },
        error: error => this.messages.showError(error),
      });
    context.subscriptions.add(context.optionRequest);
  }

  private defaultTagId(subcategoryId: string): string | undefined {
    return this.categories
      .flatMap(category => category.subcategories ?? [])
      .find(subcategory => subcategory.id === subcategoryId)
      ?.defaultTagId;
  }

  private findOption(options: AutoCompleteModel[], id?: string, fallback?: AutoCompleteModel): AutoCompleteModel | null {
    return id ? options.find(item => item.id === id) ?? (fallback?.id === id ? fallback : null) : null;
  }

  private toPayload(): PropertyErrandSubCategoryResponsibility[] {
    return this.rows.map(row => {
      const value = row.form.getRawValue();
      return {
        id: value.id,
        errandSubCategoryId: value.errandSubCategoryId,
        resourceId: value.resourceId?.id ?? null,
        executorGroupId: value.executorGroupId?.id ?? null,
        executorWebUserId: value.executorWebUserId?.id ?? null,
      };
    });
  }

  private mergeOptions(
    sources: ErrandAssignmentOptions[],
    selector: (source: ErrandAssignmentOptions) => ErrandAssignmentOption[]
  ): AutoCompleteModel[] {
    const distinct = new Map<string, ErrandAssignmentOption>();
    sources.flatMap(selector).forEach(item => distinct.set(item.id, item));
    return [...distinct.values()]
      .sort((left, right) => left.name.localeCompare(right.name))
      .map(item => new AutoCompleteModel(item.name, item.id));
  }
}
